import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, Label, DateInput } from '../ui';
import { XIcon, ChevronDownIcon, CalendarIcon } from '../ui/Icons';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface DateFilterOption {
  label: string;
  value: string;
  getRange: () => DateRange;
  icon?: React.ReactNode;
}

interface DateFilterDropdownProps {
  label: string;
  selectedValue: string | null;
  onFilterChange: (value: string | null, range?: DateRange) => void;
  placeholder?: string;
}

const DateFilterDropdown: React.FC<DateFilterDropdownProps> = ({
  label,
  selectedValue,
  onFilterChange,
  placeholder = 'All Dates',
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const now = new Date();
  // Create today as UTC date to avoid timezone issues
  const today = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  ));

  const formatDate = (date: Date): string => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  const formatDateRange = (start: Date, end: Date): string => {
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const getDateOptions = (): DateFilterOption[] => {
    const todayUTC = new Date(Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate()
    ));

    const yesterday = new Date(todayUTC);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);

    const currentWeekStart = new Date(todayUTC);
    currentWeekStart.setUTCDate(todayUTC.getUTCDate() - todayUTC.getUTCDay());
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setUTCDate(currentWeekStart.getUTCDate() + 6);

    const previousWeekStart = new Date(currentWeekStart);
    previousWeekStart.setUTCDate(currentWeekStart.getUTCDate() - 7);
    const previousWeekEnd = new Date(currentWeekStart);
    previousWeekEnd.setUTCDate(currentWeekStart.getUTCDate() - 1);

    const currentMonthStart = new Date(Date.UTC(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth(), 1));
    const currentMonthEnd = new Date(Date.UTC(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth() + 1, 0));

    const previousMonthStart = new Date(Date.UTC(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth() - 1, 1));
    const previousMonthEnd = new Date(Date.UTC(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth(), 0));

    const currentYearStart = new Date(Date.UTC(todayUTC.getUTCFullYear(), 0, 1));
    const currentYearEnd = new Date(Date.UTC(todayUTC.getUTCFullYear(), 11, 31));

    const previousYearStart = new Date(Date.UTC(todayUTC.getUTCFullYear() - 1, 0, 1));
    const previousYearEnd = new Date(Date.UTC(todayUTC.getUTCFullYear() - 1, 11, 31));

    const calendarIcon = (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );

    return [
      {
        label: 'All Dates',
        value: 'all',
        getRange: () => ({ start: null, end: null }),
        icon: calendarIcon,
      },
      {
        label: 'Today',
        value: 'today',
        getRange: () => ({ start: todayUTC, end: todayUTC }),
        icon: (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        ),
      },
      {
        label: 'Yesterday',
        value: 'yesterday',
        getRange: () => ({ start: yesterday, end: yesterday }),
        icon: calendarIcon,
      },
      {
        label: 'Current Week',
        value: 'currentWeek',
        getRange: () => ({ start: currentWeekStart, end: currentWeekEnd }),
        icon: calendarIcon,
      },
      {
        label: 'Current Month',
        value: 'currentMonth',
        getRange: () => ({ start: currentMonthStart, end: currentMonthEnd }),
        icon: calendarIcon,
      },
      {
        label: 'Current Year',
        value: 'currentYear',
        getRange: () => ({ start: currentYearStart, end: currentYearEnd }),
        icon: calendarIcon,
      },
      {
        label: 'Previous Week',
        value: 'previousWeek',
        getRange: () => ({ start: previousWeekStart, end: previousWeekEnd }),
        icon: calendarIcon,
      },
      {
        label: 'Previous Month',
        value: 'previousMonth',
        getRange: () => ({ start: previousMonthStart, end: previousMonthEnd }),
        icon: calendarIcon,
      },
      {
        label: 'Previous Year',
        value: 'previousYear',
        getRange: () => ({ start: previousYearStart, end: previousYearEnd }),
        icon: calendarIcon,
      },
    ];
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dateOptions = useMemo(() => getDateOptions(), []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (dropdownRef.current?.contains(target)) return;
      // DateInput calendars render in a portal on document.body.
      if (target instanceof Element && target.closest('[data-date-input-dropdown]')) return;
      setIsOpen(false);
      setShowCustomRange(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (option: DateFilterOption) => {
    if (option.value === 'custom') {
      setShowCustomRange(true);
    } else {
      const range = option.getRange();
      onFilterChange(option.value, range);
      setIsOpen(false);
    }
  };

  const handleCustomRangeApply = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      onFilterChange('custom', { start, end });
      setIsOpen(false);
      setShowCustomRange(false);
    }
  };

  const handleClear = () => {
    onFilterChange(null);
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomRange(false);
  };

  const formatDateForInput = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const displayText = useMemo(() => {
    if (!selectedValue || selectedValue === 'all') {
      return placeholder;
    }
    const option = dateOptions.find((opt) => opt.value === selectedValue);
    if (option) {
      if (selectedValue === 'custom') {
        if (customStartDate && customEndDate) {
          return `${formatDateForInput(new Date(customStartDate))} - ${formatDateForInput(new Date(customEndDate))}`;
        }
        return 'Custom Range';
      }
      return option.label;
    }
    return placeholder;
  }, [selectedValue, dateOptions, customStartDate, customEndDate, placeholder]);

  return (
    <div className="relative" ref={dropdownRef}>
      {label ? <Label className="mb-2 text-xs">{label}</Label> : null}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm outline-none focus:ring-2 focus:ring-indigo-500/25',
            colors.surface,
            colors.border,
          )}
        >
          <div className="flex min-w-0 items-center space-x-2">
            <CalendarIcon className={cn('h-4 w-4 shrink-0', colors.primaryFg)} />
            <span className={cn('truncate', !selectedValue || selectedValue === 'all' ? colors.textMuted : colors.text)}>
              {displayText}
            </span>
          </div>
          <ChevronDownIcon
            className={cn('h-4 w-4 shrink-0 transition-transform', colors.textMuted, isOpen && 'rotate-180')}
          />
        </button>

        {isOpen && (
          <div
            className={cn(
              'absolute left-0 z-50 mt-1 overflow-hidden rounded-xl border shadow-lg',
              colors.border,
              colors.surface,
              showCustomRange
                ? 'max-h-[500px] w-[min(340px,calc(100vw-2rem))]'
                : 'max-h-96 w-max min-w-[280px] max-w-[min(320px,calc(100vw-2rem))]',
            )}
          >
            {!showCustomRange ? (
              <div className="max-h-80 overflow-y-auto">
                {dateOptions.map((option) => {
                  const isSelected = selectedValue === option.value;
                  const range = option.getRange();
                  const rangeText = range.start && range.end ? formatDateRange(range.start, range.end) : '';

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleOptionSelect(option)}
                      className={cn(
                        'flex w-full cursor-pointer items-center justify-between px-3 py-2',
                        colors.interactiveHover,
                        isSelected && colors.info,
                      )}
                    >
                      <div className="flex flex-1 items-center space-x-2">
                        <div className={colors.primaryFg}>{option.icon}</div>
                        <div className="flex flex-col items-start">
                          <span className={cn('text-sm', colors.text)}>{option.label}</span>
                          {rangeText && (
                            <span className={cn('text-xs', colors.textMuted)}>{rangeText}</span>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <svg className={cn('h-4 w-4', colors.primaryFg)} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={() => setShowCustomRange(true)}
                  className={cn(
                    'flex w-full cursor-pointer items-center justify-between px-3 py-2',
                    colors.interactiveHover,
                    selectedValue === 'custom' && colors.info,
                  )}
                >
                  <div className="flex flex-1 items-center space-x-2">
                    <svg className={cn('h-4 w-4', colors.primaryFg)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c1.162-.5 2.385-.5 3.35 0a5.5 5.5 0 011.413 1.413c.5 1.162.5 2.385 0 3.35a5.5 5.5 0 01-1.413 1.413c-1.162.5-2.385.5-3.35 0a5.5 5.5 0 01-1.413-1.413c-.5-1.162-.5-2.385 0-3.35a5.5 5.5 0 011.413-1.413z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className={cn('text-sm', colors.text)}>Custom Range</span>
                  </div>
                  {selectedValue === 'custom' && (
                    <svg className={cn('h-4 w-4', colors.primaryFg)} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            ) : (
              <div className="w-full p-4">
                <div className={cn('mb-3 flex items-center justify-between border-b pb-2', colors.border)}>
                  <h3 className={cn('text-sm font-semibold', colors.text)}>{label || 'Date range'}</h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowCustomRange(false)}
                    title="Back"
                  >
                    <XIcon className="h-5 w-5" />
                  </Button>
                </div>

                <div className={cn('mb-4 rounded-lg border p-2.5', colors.border, colors.info)}>
                  <div className="flex items-center space-x-2">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c1.162-.5 2.385-.5 3.35 0a5.5 5.5 0 011.413 1.413c.5 1.162.5 2.385 0 3.35a5.5 5.5 0 01-1.413 1.413c-1.162.5-2.385.5-3.35 0a5.5 5.5 0 01-1.413-1.413c-.5-1.162-.5-2.385 0-3.35a5.5 5.5 0 011.413-1.413z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-sm font-medium">Custom Range</span>
                  </div>
                </div>

                <div className="mb-4 space-y-3">
                  <div>
                    <Label className="mb-2 text-xs">Start Date</Label>
                    <DateInput
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value || '')}
                      placeholder="Select start date"
                      showClearButton={false}
                    />
                  </div>

                  <div>
                    <Label className="mb-2 text-xs">End Date</Label>
                    <DateInput
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value || '')}
                      placeholder="Select end date"
                      showClearButton={false}
                      minDate={customStartDate || undefined}
                    />
                  </div>
                </div>

                <div className={cn('flex justify-end space-x-2 border-t pt-2', colors.border)}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleCustomRangeApply}
                    disabled={!customStartDate || !customEndDate}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DateFilterDropdown;
